import { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import * as api from '../lib/api';
import { GUIA_I_PREGUNTAS, GUIA_II_PREGUNTAS, GUIA_III_PREGUNTAS } from '../data';
import { Shield, ChevronRight, ChevronLeft, Check, AlertCircle, Loader, Download, Upload } from 'lucide-react';

const GUIA_MAP = { guiaI: GUIA_I_PREGUNTAS, guiaII: GUIA_II_PREGUNTAS, guiaIII: GUIA_III_PREGUNTAS };
const GUIA_NAMES = { guiaI: 'Guía I — ATS', guiaII: 'Guía II — FRP', guiaIII: 'Guía III — Entorno Organizacional' };

export default function PortalPage() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paso, setPaso] = useState(null);
  const [saving, setSaving] = useState(false);
  const [firmo, setFirmo] = useState(false);
  const [archivoFirmado, setArchivoFirmado] = useState(null);

  // Cuestionario state
  const EDADES = ['15 – 19','20 – 24','25 – 29','30 – 34','35 – 39','40 – 44','45 – 49','50 – 54','55 – 59','60 – 64','65 – 69','70 o más'];
  const TIEMPOS = ['Menos de 6 meses','Entre 6 meses y 1 año','Entre 1 a 4 años','Entre 5 a 9 años','Entre 10 a 14 años','Entre 15 a 19 años','Entre 20 a 24 años','25 años o más'];
  const [ficha, setFicha] = useState({
    sexo: '', edad: '', estadoCivil: '', nivelEstudios: '',
    puesto: '', area: '', tipoPuesto: '', tipoContratacion: '',
    tipoPersonal: '', tipoJornada: '', rotacionTurnos: '',
    tiempoPuesto: '', tiempoExperiencia: '',
  });

  // Cuestionario state
  const [respuestas, setRespuestas] = useState({});
  const [preguntaIdx, setPreguntaIdx] = useState(0);

  // Pre-compute flat question list with persistent section intro per question.
  // guia-i.js: flat array — track current intro text from tipo:"seccion"|"intro" items.
  // guia-ii/iii.js: section objects — each question inherits its section's intro field.
  const flatQuestions = useMemo(() => {
    if (!paso?.startsWith('guia')) return [];
    if (paso === 'guiaI') {
      const items = [];
      let currentIntro = null;
      GUIA_I_PREGUNTAS.forEach(p => {
        if (p.tipo === 'intro' || p.tipo === 'seccion') {
          currentIntro = p.texto;
        } else if (p.categoria) {
          items.push({ id: p.id, texto: p.texto, sectionIntro: currentIntro });
        }
      });
      return items;
    }
    const source = paso === 'guiaII' ? GUIA_II_PREGUNTAS : GUIA_III_PREGUNTAS;
    const items = [];
    let lastIntro = null;
    source.forEach(seccion => {
      if (seccion.intro) {
        lastIntro = seccion.intro;
      }
      const introForSection = seccion.intro ? seccion.intro : (seccion.visualContinue ? lastIntro : null);
      // Insert filter question before conditional preguntas
      if (seccion.preguntaFiltro) {
        items.push({
          id: seccion.preguntaFiltro.id,
          texto: seccion.preguntaFiltro.texto,
          num: null,
          sectionIntro: introForSection,
          isFilter: true,
        });
      }
      (seccion.preguntas || []).forEach(p => {
        items.push({
          id: p.id, texto: p.texto, num: p.num, sectionIntro: introForSection,
          filtroId: seccion.preguntaFiltro ? seccion.preguntaFiltro.id : null,
        });
      });
    });
    return items;
  }, [paso]);

  useEffect(() => {
    api.getPortalInfo(token)
      .then(data => { setInfo(data); setPaso(data.paso_actual); setLoading(false); })
      .catch(err => {
        // Link ya cerrado (completado/rechazado) — mostrar la pantalla final
        // correspondiente en vez de un error, usando lo que el backend ya
        // mandó en el 410 (no hace falta reintentar contra un link cerrado).
        if (err.code === 'CLOSED' && err.closedInfo) {
          setInfo(err.closedInfo);
          setPaso(err.closedInfo.status);
          setLoading(false);
          return;
        }
        setError(err.message); setLoading(false);
      });
  }, [token]);

  // Si el backend rechaza un guardado porque el link ya avanzó de paso
  // (ej. una reintentada tras perder la respuesta de una llamada que sí tuvo
  // éxito), no es un error real para el trabajador — solo hay que
  // re-sincronizar con el estado actual del servidor en vez de mostrarle una
  // alerta confusa sobre un paso que, de hecho, ya se completó.
  const handlePortalError = async (err) => {
    if (err.code === 'STEP_MISMATCH') {
      try {
        const fresh = await api.getPortalInfo(token);
        setInfo(fresh);
        setPaso(fresh.paso_actual);
        setRespuestas({});
        setPreguntaIdx(0);
        return;
      } catch (e) { /* fall through to alert below */ }
    }
    alert(err.message);
  };

  const accent = '#6366f1';
  const wrap = { minHeight: '100vh', background: '#f8fafc', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif', overflowY: 'auto' };

  // Override global overflow:hidden for portal
  useEffect(() => {
    document.documentElement.style.overflow = 'auto';
    document.body.style.overflow = 'auto';
    document.getElementById('root').style.overflow = 'auto';
    return () => {
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
      document.getElementById('root').style.overflow = '';
    };
  }, []);
  const card = { maxWidth: 520, margin: '0 auto', padding: '24px 20px' };
  const btn = (primary) => ({
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
    padding: '14px 28px', borderRadius: 12,
    fontSize: '0.95rem', fontWeight: 600, cursor: saving ? 'wait' : 'pointer',
    background: primary ? accent : '#fff', color: primary ? '#fff' : '#475569',
    boxShadow: primary ? '0 4px 14px rgba(99,102,241,0.25)' : 'none',
    border: primary ? 'none' : '1px solid #e2e8f0', width: '100%', opacity: saving ? 0.7 : 1,
  });

  // ── Loading / Error ─────────────────────────────────────
  if (loading) return (
    <div style={{ ...wrap, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ textAlign: 'center', color: '#64748b' }}>
        <Loader size={32} style={{ animation: 'spin 1s linear infinite' }} />
        <p style={{ marginTop: 12 }}>Cargando...</p>
        <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
      </div>
    </div>
  );

  if (error) return (
    <div style={{ ...wrap, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ textAlign: 'center', maxWidth: 400, padding: 20 }}>
        <AlertCircle size={48} style={{ color: '#ef4444', marginBottom: 16 }} />
        <h2 style={{ fontSize: '1.3rem', color: '#1e293b', marginBottom: 8 }}>No se pudo cargar</h2>
        <p style={{ color: '#64748b', lineHeight: 1.5 }}>{error}</p>
      </div>
    </div>
  );

  // ── Bienvenida / Consentimiento ─────────────────────────
  if (paso === 'consentimiento') {
    const fechaHoy = new Date().toLocaleDateString('es-MX', { year: 'numeric', month: 'long', day: 'numeric' });
    const nombreCorto = info?.trabajador_nombre?.split(' ')[0] || '';
    const empresa = info?.empresa_nombre || '';
    const centro = info?.centro_nombre || '';
    const trabajador = info?.trabajador_nombre || '';

    const handleConsent = async (acepta) => {
      setSaving(true);
      try {
        const fecha = new Date().toISOString().split('T')[0];
        let archivoData = '';
        if (archivoFirmado && acepta) {
          archivoData = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result.split(',')[1]); // base64 only
            reader.onerror = () => reject(new Error('Error leyendo archivo'));
            reader.readAsDataURL(archivoFirmado);
          });
        }
        const res = await api.savePortalConsentimiento(token, {
          acepta,
          fecha,
          metodo: 'Portal digital',
          archivo: archivoFirmado?.name || '',
          archivoTamano: archivoFirmado ? (archivoFirmado.size / 1024).toFixed(1) + ' KB' : '',
          archivoTipo: archivoFirmado?.type || '',
          archivoData,
        });
        setPaso(res.next);
      } catch (err) { await handlePortalError(err); }
      setSaving(false);
    };

    const generarPDF = async () => {
      const { jsPDF } = await import('jspdf');
      const doc = new jsPDF({ unit: 'mm', format: 'letter' });
      const W = 216, mg = 22, cw = W - mg * 2;
      let y = 20;
      const addText = (text, size, style, align) => {
        doc.setFontSize(size); doc.setFont('helvetica', style || 'normal');
        const lines = doc.splitTextToSize(text, cw);
        if (align === 'center') doc.text(lines, W / 2, y, { align: 'center' });
        else doc.text(lines, mg, y);
        y += lines.length * (size * 0.4) + 1;
      };
      addText('CONSENTIMIENTO INFORMADO', 13, 'bold', 'center');
      addText('Evaluacion de Factores de Riesgo Psicosocial - NOM-035-STPS-2018', 9, 'normal', 'center');
      y += 1; doc.setDrawColor(60); doc.line(mg, y, W - mg, y); y += 4;
      doc.setFillColor(245, 245, 245); doc.rect(mg, y - 2, cw, 18, 'F');
      addText('Empresa: ' + empresa + '    Centro: ' + centro, 8, 'normal');
      addText('Trabajador(a): ' + trabajador + '    Fecha: ' + fechaHoy, 8, 'normal');
      y += 3;
      addText('Yo, ' + trabajador + ', declaro que he sido informado(a) sobre lo siguiente:', 9, 'normal');
      y += 1;
      const clausulas = [
        ['1. Proposito', 'La presente evaluacion tiene como objetivo identificar y analizar los factores de riesgo psicosocial en el centro de trabajo "' + centro + '" de la empresa "' + empresa + '", en cumplimiento con la NOM-035-STPS-2018.'],
        ['2. Informacion que se recabara', 'Se recabaran datos personales generales (sexo, estado civil, nivel de estudios, puesto, antiguedad, tipo de contratacion y jornada laboral), asi como respuestas a cuestionarios sobre acontecimientos traumaticos severos y factores de riesgo psicosocial.'],
        ['3. Confidencialidad', 'Mis respuestas seran tratadas de forma estrictamente confidencial. Los resultados se presentaran de manera agregada y en ningun caso se identificara de forma individual a los trabajadores.'],
        ['4. Uso de los resultados', 'Los resultados seran utilizados exclusivamente para disenar e implementar medidas de prevencion y acciones de control conforme a la NOM-035-STPS-2018.'],
        ['5. Participacion voluntaria', 'Mi participacion es voluntaria. Puedo decidir no responder alguna pregunta o retirarme en cualquier momento sin consecuencia alguna en mi situacion laboral.'],
        ['6. Responsable', 'La empresa designara a una persona responsable a quien podre dirigirme para cualquier duda o aclaracion.'],
      ];
      clausulas.forEach(([t, txt]) => { addText(t, 9, 'bold'); addText(txt, 9, 'normal'); y += 2; });
      y += 2; doc.setDrawColor(180); doc.line(mg, y, W - mg, y); y += 4;
      addText('Declaro que he leido y comprendido la informacion anterior y que acepto participar de forma voluntaria en la evaluacion conforme a la NOM-035-STPS-2018.', 9, 'italic');
      y += 14;
      doc.setDrawColor(60); doc.line(W / 2 - 40, y, W / 2 + 40, y); y += 5;
      doc.setFontSize(9); doc.setFont('helvetica', 'bold');
      doc.text(trabajador, W / 2, y, { align: 'center' }); y += 4;
      doc.setFont('helvetica', 'normal');
      doc.text('Nombre y firma del trabajador(a)', W / 2, y, { align: 'center' });
      const pdfData = doc.output('datauristring');
      const link = document.createElement('a');
      link.href = pdfData;
      link.download = 'Consentimiento_' + trabajador.replace(/\s+/g, '_') + '.pdf';
      link.style.display = 'none';
      document.body.appendChild(link); link.click(); document.body.removeChild(link);
    };

    const clausulaStyle = { padding: '10px 14px', background: '#f8fafc', borderRadius: 10, marginBottom: 10, borderLeft: '3px solid ' + accent };
    const clausulaTitulo = { fontSize: '0.78rem', fontWeight: 700, color: accent, marginBottom: 4 };
    const clausulaTexto = { fontSize: '0.82rem', color: '#475569', lineHeight: 1.6, margin: 0 };

    return (
      <div style={wrap}>
        <div style={card}>
          <Header info={info} />
          <div style={{ background: '#fff', borderRadius: 16, padding: 28, boxShadow: '0 2px 12px rgba(0,0,0,0.04)', border: '1px solid #f0f0f0' }}>
            <h2 style={{ fontSize: '1.15rem', fontWeight: 700, color: '#1e293b', margin: '0 0 4px', textAlign: 'center' }}>Consentimiento informado</h2>
            <p style={{ fontSize: '0.75rem', color: '#94a3b8', textAlign: 'center', margin: '0 0 16px' }}>NOM-035-STPS-2018</p>

            {/* Datos */}
            <div style={{ background: '#f1f5f9', borderRadius: 10, padding: '10px 14px', marginBottom: 16, fontSize: '0.8rem', color: '#475569', lineHeight: 1.6 }}>
              <div><strong>Empresa:</strong> {empresa}</div>
              <div><strong>Centro:</strong> {centro}</div>
              <div><strong>Trabajador(a):</strong> {trabajador}</div>
              <div><strong>Fecha:</strong> {fechaHoy}</div>
            </div>

            <p style={{ fontSize: '0.85rem', color: '#334155', lineHeight: 1.6, marginBottom: 16 }}>
              Yo, <strong>{trabajador}</strong>, declaro que he sido informado(a) sobre lo siguiente:
            </p>

            {/* Clausulas */}
            <div style={clausulaStyle}>
              <div style={clausulaTitulo}>1. Proposito de la evaluacion</div>
              <p style={clausulaTexto}>La presente evaluacion tiene como objetivo identificar y analizar los factores de riesgo psicosocial en el centro de trabajo <strong>{centro}</strong> de la empresa <strong>{empresa}</strong>, en cumplimiento con la NOM-035-STPS-2018.</p>
            </div>
            <div style={clausulaStyle}>
              <div style={clausulaTitulo}>2. Informacion que se recabara</div>
              <p style={clausulaTexto}>Se recabaran datos personales generales (sexo, estado civil, nivel de estudios, puesto, antiguedad, tipo de contratacion y jornada laboral), asi como respuestas a cuestionarios sobre acontecimientos traumaticos severos y factores de riesgo psicosocial.</p>
            </div>
            <div style={clausulaStyle}>
              <div style={clausulaTitulo}>3. Confidencialidad</div>
              <p style={clausulaTexto}>Tus respuestas seran tratadas de forma estrictamente confidencial. Los resultados se presentaran de manera agregada y en ningun caso se identificara de forma individual a los trabajadores.</p>
            </div>
            <div style={clausulaStyle}>
              <div style={clausulaTitulo}>4. Uso de los resultados</div>
              <p style={clausulaTexto}>Los resultados seran utilizados exclusivamente para disenar e implementar medidas de prevencion y acciones de control conforme a la NOM-035-STPS-2018.</p>
            </div>
            <div style={clausulaStyle}>
              <div style={clausulaTitulo}>5. Participacion voluntaria</div>
              <p style={clausulaTexto}>Tu participacion es voluntaria. Puedes decidir no responder alguna pregunta o retirarte en cualquier momento sin consecuencia alguna en tu situacion laboral.</p>
            </div>
            <div style={clausulaStyle}>
              <div style={clausulaTitulo}>6. Responsable de la evaluacion</div>
              <p style={clausulaTexto}>La empresa designara a una persona responsable a quien podras dirigirte para cualquier duda o aclaracion.</p>
            </div>

            {/* Declaracion */}
            <div style={{ background: '#fefce8', border: '1px solid #fde68a', borderRadius: 10, padding: '12px 14px', margin: '16px 0', fontSize: '0.82rem', color: '#92400e', lineHeight: 1.6 }}>
              Declaro que he leido y comprendido la informacion contenida en este documento. Manifiesto de manera libre y voluntaria mi consentimiento para participar en la evaluacion de factores de riesgo psicosocial conforme a la NOM-035-STPS-2018.
            </div>

            {/* Download PDF */}
            <button onClick={generarPDF} style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
              width: '100%', padding: '12px', borderRadius: 10,
              background: '#f1f5f9', border: '1px solid #e2e8f0', color: '#475569',
              fontSize: '0.85rem', fontWeight: 600, cursor: 'pointer', marginBottom: 16,
            }}>
              <Download size={16} /> Descargar PDF para firmar
            </button>

            {/* Upload signed PDF */}
            <div style={{
              padding: '16px', borderRadius: 12, marginBottom: 16,
              border: archivoFirmado ? '1px solid rgba(16,185,129,0.3)' : '2px dashed #cbd5e1',
              background: archivoFirmado ? 'rgba(16,185,129,0.04)' : '#fafbfc',
              textAlign: 'center',
            }}>
              {archivoFirmado ? (
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginBottom: 8 }}>
                    <Check size={18} style={{ color: '#10b981' }} />
                    <span style={{ fontSize: '0.85rem', fontWeight: 600, color: '#10b981' }}>Archivo cargado</span>
                  </div>
                  <div style={{ fontSize: '0.78rem', color: '#64748b', marginBottom: 8 }}>{archivoFirmado.name} ({(archivoFirmado.size / 1024).toFixed(0)} KB)</div>
                  <button onClick={() => setArchivoFirmado(null)} style={{
                    padding: '6px 14px', borderRadius: 8, border: '1px solid #e2e8f0',
                    background: '#fff', color: '#64748b', fontSize: '0.78rem', cursor: 'pointer',
                  }}>Reemplazar archivo</button>
                </div>
              ) : (
                <label style={{ cursor: 'pointer', display: 'block' }}>
                  <input type="file" accept=".pdf,.jpg,.jpeg,.png" style={{ display: 'none' }}
                    onChange={e => { if (e.target.files?.[0]) setArchivoFirmado(e.target.files[0]); }} />
                  <Upload size={24} style={{ color: '#94a3b8', marginBottom: 8 }} />
                  <div style={{ fontSize: '0.85rem', fontWeight: 600, color: '#475569', marginBottom: 4 }}>Subir consentimiento firmado</div>
                  <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>PDF, JPG o PNG — Toca para seleccionar archivo</div>
                </label>
              )}
            </div>

            {/* Checkbox */}
            <label style={{
              display: 'flex', alignItems: 'flex-start', gap: 10, cursor: archivoFirmado ? 'pointer' : 'not-allowed',
              marginBottom: 16, padding: '12px 14px',
              background: firmo ? `${accent}08` : '#f9fafb',
              borderRadius: 10, border: firmo ? `1px solid ${accent}30` : '1px solid #e5e7eb',
              transition: 'all 0.2s', opacity: archivoFirmado ? 1 : 0.5,
            }}>
              <input type="checkbox" checked={firmo} disabled={!archivoFirmado}
                onChange={e => setFirmo(e.target.checked)}
                style={{ marginTop: 2, accentColor: accent, width: 18, height: 18 }} />
              <span style={{ fontSize: '0.85rem', color: firmo ? '#1e293b' : '#64748b', lineHeight: 1.5 }}>
                Confirmo que descargue, lei y firme el documento de consentimiento informado.
              </span>
            </label>

            {/* Actions */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <button onClick={() => handleConsent(true)} disabled={saving || !firmo || !archivoFirmado} style={{ ...btn(true), opacity: (!firmo || !archivoFirmado || saving) ? 0.4 : 1, cursor: (!firmo || !archivoFirmado || saving) ? 'not-allowed' : 'pointer' }}>
                <Check size={18} /> Continuar
              </button>
              <button onClick={() => handleConsent(false)} disabled={saving} style={btn(false)}>
                Tengo dudas — contactar a RH
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Rechazado ───────────────────────────────────────────
  if (paso === 'rechazado') return (
    <div style={wrap}>
      <div style={card}>
        <Header info={info} />
        <div style={{ background: '#fff', borderRadius: 16, padding: 28, textAlign: 'center', boxShadow: '0 2px 12px rgba(0,0,0,0.04)' }}>
          <div style={{ fontSize: '2.5rem', marginBottom: 12 }}>🙏</div>
          <h2 style={{ fontSize: '1.2rem', color: '#1e293b', marginBottom: 8 }}>Gracias</h2>
          <p style={{ color: '#64748b', fontSize: '0.9rem' }}>Has decidido no participar. Tu decisión ha sido registrada. Puedes cerrar esta ventana.</p>
        </div>
      </div>
    </div>
  );

  // ── Ficha ───────────────────────────────────────────────
  if (paso === 'ficha') {
    const handleFicha = async () => {
      setSaving(true);
      try {
        const res = await api.savePortalFicha(token, ficha);
        setPaso(res.next);
        setPreguntaIdx(0);
        setRespuestas([]);
      } catch (err) { await handlePortalError(err); }
      setSaving(false);
    };

    const sel = (field, label, options) => (
      <div key={field} style={{ marginBottom: 14 }}>
        <label style={{ fontSize: '0.8rem', fontWeight: 600, color: '#374151', marginBottom: 4, display: 'block' }}>{label}</label>
        <select value={ficha[field]} onChange={e => setFicha(f => ({ ...f, [field]: e.target.value }))}
          style={{ width: '100%', padding: '12px 14px', borderRadius: 10, border: '1px solid #e5e7eb', background: '#f9fafb', fontSize: '0.88rem', color: '#1e293b' }}>
          <option value="">Seleccionar...</option>
          {options.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
      </div>
    );

    return (
      <div style={wrap}>
        <div style={card}>
          <Header info={info} subtitle="Paso 2 — Datos generales" />
          <div style={{ background: '#fff', borderRadius: 16, padding: 28, boxShadow: '0 2px 12px rgba(0,0,0,0.04)' }}>
            <h2 style={{ fontSize: '1.1rem', fontWeight: 700, color: '#1e293b', margin: '0 0 6px' }}>Ficha del trabajador</h2>
            <p style={{ fontSize: '0.78rem', color: '#94a3b8', margin: '0 0 20px' }}>Guía de Referencia V — NOM-035-STPS-2018</p>
            {sel('sexo', 'Sexo *', ['Masculino', 'Femenino'])}
            {sel('edad', 'Edad (años) *', EDADES)}
            {sel('estadoCivil', 'Estado civil', ['Soltero(a)', 'Casado(a)', 'Unión libre', 'Divorciado(a)', 'Viudo(a)'])}
            {sel('nivelEstudios', 'Nivel de estudios', ['Sin formación', 'Primaria incompleta', 'Primaria terminada', 'Secundaria incompleta', 'Secundaria terminada', 'Preparatoria o bachillerato incompleta', 'Preparatoria o bachillerato terminada', 'Técnico superior incompleta', 'Técnico superior terminada', 'Licenciatura incompleta', 'Licenciatura terminada', 'Maestría incompleta', 'Maestría terminada', 'Doctorado incompleto', 'Doctorado terminado'])}
            <input value={ficha.puesto} onChange={e => setFicha(f => ({ ...f, puesto: e.target.value }))} placeholder="Ocupación / Puesto *"
              style={{ width: '100%', padding: '12px 14px', borderRadius: 10, border: '1px solid #e5e7eb', background: '#f9fafb', fontSize: '0.88rem', marginBottom: 14, boxSizing: 'border-box' }} />
            <input value={ficha.area} onChange={e => setFicha(f => ({ ...f, area: e.target.value }))} placeholder="Departamento / Área *"
              style={{ width: '100%', padding: '12px 14px', borderRadius: 10, border: '1px solid #e5e7eb', background: '#f9fafb', fontSize: '0.88rem', marginBottom: 14, boxSizing: 'border-box' }} />
            {sel('tipoPuesto', 'Tipo de puesto *', ['Operativo', 'Profesional o técnico', 'Supervisor', 'Gerente'])}
            {sel('tipoContratacion', 'Tipo de contratación *', ['Por obra o proyecto', 'Por tiempo determinado (temporal)', 'Tiempo indeterminado', 'Honorarios'])}
            {sel('tipoPersonal', 'Tipo de personal', ['Sindicalizado', 'Confianza', 'Ninguno'])}
            {sel('tipoJornada', 'Tipo de jornada *', ['Fijo diurno (entre las 6:00 y 20:00 hrs)', 'Fijo nocturno (entre las 20:00 y 6:00 hrs)', 'Fijo mixto (combinación de nocturno y diurno)'])}
            {sel('rotacionTurnos', 'Rotación de turnos', ['Sí', 'No'])}
            {sel('tiempoPuesto', 'Tiempo en el puesto actual', TIEMPOS)}
            {sel('tiempoExperiencia', 'Experiencia laboral total', TIEMPOS)}
            <div style={{ marginTop: 6 }}>
              <button onClick={handleFicha} disabled={saving || !ficha.sexo || !ficha.edad || !ficha.puesto || !ficha.area || !ficha.tipoPuesto || !ficha.tipoContratacion || !ficha.tipoJornada} style={{...btn(true), opacity: (ficha.sexo && ficha.edad && ficha.puesto && ficha.area && ficha.tipoPuesto && ficha.tipoContratacion && ficha.tipoJornada) ? 1 : 0.5}}>
                Continuar <ChevronRight size={18} />
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Cuestionarios ───────────────────────────────────────
  if (paso?.startsWith('guia')) {
    const guiaName = GUIA_NAMES[paso] || paso;
    const isGI = paso === 'guiaI';

    // Initialize respuestas as object keyed by question ID
    if (typeof respuestas !== 'object' || Array.isArray(respuestas) || Object.keys(respuestas).length === 0) {
      const init = {};
      flatQuestions.forEach(q => { init[q.id] = null; });
      setRespuestas(init);
      setPreguntaIdx(0);
      return null;
    }

    // Determine which questions are skipped (conditional where filter = false)
    const isSkipped = (q) => q.filtroId && respuestas[q.filtroId] === false;

    // Active questions = all non-skipped
    const activeQuestions = flatQuestions.filter(q => !isSkipped(q));
    const total = activeQuestions.length;

    // Find current question — ensure preguntaIdx points to a non-skipped question
    let currentQ = flatQuestions[preguntaIdx];
    if (currentQ && isSkipped(currentQ)) {
      // Jump forward to next non-skipped
      let next = preguntaIdx + 1;
      while (next < flatQuestions.length && isSkipped(flatQuestions[next])) next++;
      if (next < flatQuestions.length) { setPreguntaIdx(next); return null; }
      // All remaining are skipped — go to last active
      let prev = preguntaIdx - 1;
      while (prev >= 0 && isSkipped(flatQuestions[prev])) prev--;
      if (prev >= 0) { setPreguntaIdx(prev); return null; }
    }
    if (!currentQ) return null;

    const activeIdx = activeQuestions.findIndex(q => q.id === currentQ.id);
    const answeredCount = activeQuestions.filter(q => respuestas[q.id] !== null).length;
    const allAnswered = answeredCount === total;
    const isLast = activeIdx === total - 1;
    const progress = Math.round((answeredCount / total) * 100);

    // Navigate to next non-skipped question
    const goNext = () => {
      let next = preguntaIdx + 1;
      while (next < flatQuestions.length && isSkipped(flatQuestions[next])) next++;
      if (next < flatQuestions.length) setPreguntaIdx(next);
    };
    const goPrev = () => {
      let prev = preguntaIdx - 1;
      while (prev >= 0 && isSkipped(flatQuestions[prev])) prev--;
      if (prev >= 0) setPreguntaIdx(prev);
    };

    const handleAnswer = (val) => {
      const next = { ...respuestas, [currentQ.id]: val };
      // If this is a filter question answered "No", clear its conditional answers
      if (currentQ.isFilter && val === false) {
        flatQuestions.forEach(q => {
          if (q.filtroId === currentQ.id) next[q.id] = null;
        });
      }
      setRespuestas(next);
      if (!isLast) {
        setTimeout(() => goNext(), 200);
      }
    };

    const handleSubmit = async () => {
      if (!allAnswered) { alert('Faltan preguntas por responder'); return; }
      setSaving(true);
      try {
        // Send only answered questions (exclude skipped conditionals with null values)
        const toSend = {};
        Object.entries(respuestas).forEach(([k, v]) => {
          if (v !== null) toSend[k] = v;
        });
        const res = await api.savePortalCuestionario(token, paso, toSend);
        setPaso(res.next);
        setRespuestas({});
        setPreguntaIdx(0);
      } catch (err) { await handlePortalError(err); }
      setSaving(false);
    };

    // Answer options depend on question type
    const isFilter = currentQ.isFilter;
    const options = (isGI || isFilter)
      ? [{ label: 'Si', val: true }, { label: 'No', val: false }]
      : [
          { label: 'Siempre', val: 0 },
          { label: 'Casi siempre', val: 1 },
          { label: 'Algunas veces', val: 2 },
          { label: 'Casi nunca', val: 3 },
          { label: 'Nunca', val: 4 },
        ];

    const currentVal = respuestas[currentQ.id];

    // Filter instruction text
    const filterNote = isFilter
      ? 'Si tu respuesta es "Si", responderas las preguntas siguientes. Si es "No", pasaras a la siguiente seccion.'
      : null;

    return (
      <div style={wrap}>
        <div style={card}>
          <Header info={info} subtitle={guiaName} />

          {/* Progress */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
              <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>{activeIdx + 1} de {total}</span>
              <span style={{ fontSize: '0.75rem', fontWeight: 600, color: accent }}>{progress}%</span>
            </div>
            <div style={{ height: 6, background: '#e2e8f0', borderRadius: 3 }}>
              <div style={{ height: '100%', background: accent, borderRadius: 3, width: `${progress}%`, transition: 'width 0.3s' }} />
            </div>
          </div>

          {/* Section intro */}
          {currentQ.sectionIntro && (
            <div style={{ background: `${accent}08`, border: `1px solid ${accent}22`, borderRadius: 12, padding: '12px 16px', marginBottom: 14 }}>
              <p style={{ fontSize: '0.8rem', color: '#475569', lineHeight: 1.6, margin: 0 }}>
                {currentQ.sectionIntro}
              </p>
            </div>
          )}

          {/* Question */}
          <div style={{ background: '#fff', borderRadius: 16, padding: 24, boxShadow: '0 2px 12px rgba(0,0,0,0.04)', marginBottom: 16 }}>
            {currentQ.num && <div style={{ fontSize: '0.7rem', color: '#94a3b8', fontWeight: 600, marginBottom: 6 }}>Pregunta {currentQ.num}</div>}
            <p style={{ fontSize: '0.95rem', color: '#1e293b', lineHeight: 1.6, fontWeight: 500, margin: '0 0 6px' }}>
              {currentQ.texto}
            </p>
            {filterNote && (
              <p style={{ fontSize: '0.78rem', color: '#94a3b8', lineHeight: 1.5, margin: '0 0 16px', fontStyle: 'italic' }}>
                {filterNote}
              </p>
            )}

            <div style={{ display: 'flex', flexDirection: isFilter ? 'row' : 'column', gap: 8 }}>
              {options.map((opt, oi) => (
                <button key={oi} onClick={() => handleAnswer(opt.val)} style={{
                  padding: '13px 16px', borderRadius: 10, border: '1px solid',
                  borderColor: currentVal === opt.val ? accent : '#e5e7eb',
                  background: currentVal === opt.val ? `${accent}10` : '#fff',
                  color: currentVal === opt.val ? accent : '#475569',
                  fontSize: '0.88rem', fontWeight: currentVal === opt.val ? 600 : 400,
                  cursor: 'pointer', textAlign: 'center', transition: 'all 0.15s',
                  flex: isFilter ? 1 : undefined,
                }}>
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Navigation */}
          <div style={{ display: 'flex', gap: 10 }}>
            {activeIdx > 0 && (
              <button onClick={goPrev} style={{ ...btn(false), flex: 1 }}>
                <ChevronLeft size={18} /> Anterior
              </button>
            )}
            {isLast && allAnswered ? (
              <button onClick={handleSubmit} disabled={saving} style={{ ...btn(true), flex: 1 }}>
                <Check size={18} /> Finalizar guia
              </button>
            ) : (
              <button onClick={() => !isLast && goNext()}
                disabled={currentVal === null}
                style={{ ...btn(true), flex: 1, opacity: currentVal === null ? 0.4 : 1 }}>
                Siguiente <ChevronRight size={18} />
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── Completado ──────────────────────────────────────────
  if (paso === 'completado') return (
    <div style={wrap}>
      <div style={card}>
        <Header info={info} />
        <div style={{ background: '#fff', borderRadius: 16, padding: 36, textAlign: 'center', boxShadow: '0 2px 12px rgba(0,0,0,0.04)' }}>
          <div style={{ width: 64, height: 64, borderRadius: '50%', background: '#ecfdf5', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16 }}>
            <Check size={32} style={{ color: '#10b981' }} />
          </div>
          <h2 style={{ fontSize: '1.3rem', color: '#1e293b', margin: '0 0 8px' }}>¡Completado!</h2>
          <p style={{ color: '#64748b', fontSize: '0.9rem', lineHeight: 1.5, marginBottom: 8 }}>
            Tus respuestas han sido registradas. Gracias por tu participación.
          </p>
          <p style={{ color: '#94a3b8', fontSize: '0.78rem' }}>Puedes cerrar esta ventana.</p>
        </div>
      </div>
    </div>
  );

  // Fallback
  return (
    <div style={wrap}>
      <div style={card}>
        <Header info={info} />
        <div style={{ background: '#fff', borderRadius: 16, padding: 28, textAlign: 'center' }}>
          <p style={{ color: '#64748b' }}>Estado desconocido: {paso}</p>
        </div>
      </div>
    </div>
  );
}

// ── Header component ──────────────────────────────────────
function Header({ info, subtitle }) {
  return (
    <div style={{ textAlign: 'center', marginBottom: 20, paddingTop: 12 }}>
      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
        <Shield size={18} style={{ color: '#6366f1' }} />
        <span style={{ fontWeight: 700, fontSize: '0.88rem', color: '#1e293b' }}>NOM-035</span>
      </div>
      <div style={{ fontSize: '0.82rem', color: '#64748b' }}>{info?.empresa_nombre}</div>
      {info?.centro_nombre && <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>{info.centro_nombre}</div>}
      {info?.trabajador_nombre && <div style={{ fontSize: '0.88rem', fontWeight: 600, color: '#1e293b', marginTop: 8 }}>Hola, {info.trabajador_nombre.split(' ')[0]}</div>}
      {subtitle && <div style={{ fontSize: '0.72rem', color: '#6366f1', fontWeight: 600, marginTop: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{subtitle}</div>}
    </div>
  );
}
